import { ListChecks } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, pathFor, Callout,
  Diagram, MockTable, MockButton,
} from "../shared";

export default function SubTasksSection() {
  return (
    <div data-testid="guide-sub-tasks">
      <PageHeading
        icon={ListChecks}
        title="Sub-Task Assignments"
        description="When one action item needs several people pulling on it, an admin can break it into smaller pieces called sub-tasks. Each piece has its own person, instruction, due date, and status."
      />

      <Callout variant="info" title="In plain words">
        A sub-task is a small to-do under a bigger to-do. If the main item is "Prepare the board pack", the sub-tasks might be "Draft the finance section" and "Collect the photos" — each handed to a different person and tracked on its own.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Only when an item genuinely needs more than one person. If a single person can do the whole thing, skip sub-tasks entirely.
        </p>
      </Section>

      <Section title="How sub-tasks work">
        <Diagram
          title="The Sub-Tasks area on an item's detail page"
          frameLabel="Action Item detail → Sub-Tasks"
          legend={[
            <>Open the item's detail page and scroll to the <span className="text-white">Sub-Tasks</span> area.</>,
            <>Each row is one sub-task, with its own assignee, due date, and status.</>,
            <>Click <span className="text-white">Add sub-task</span> to create a new one (admins only).</>,
          ]}
        >
          <MockTable
            columns={[
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">1</span>
                Sub-task
              </span>,
              "Assignee", "Due", "Status",
            ]}
            rows={[
              [<span className="inline-flex items-center gap-1.5"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>Draft finance section</span>, "Sara K.", "12 May", <span className="text-emerald-300">Complete</span>],
              ["Collect photos", "Ravi P.", "14 May", <span className="text-amber-300">In Progress</span>],
            ]}
          />
          <MockButton label="Add sub-task" marker={3} />
        </Diagram>
        <Steps
          steps={[
            <>Open an action item's detail page and scroll to <span className="text-white">Sub-Tasks</span>.</>,
            <>Add a sub-task: instruction, assignee (from team members), due date, and an optional starting status.</>,
            <>Each sub-task tracks its own status — Pending, In Progress, or Complete.</>,
            <>The Updates page shows a small <span className="text-white">"2/5 done"</span> progress badge on items that have sub-tasks.</>,
            <>Members assigned a sub-task see it on their Updates page and can mark it complete themselves.</>,
            <>The parent item's owner gets an email when a sub-task is created, and again when it's completed.</>,
          ]}
        />
        <p className="text-xs text-white/40 italic">
          Sub-tasks are optional. Every sub-task change is also saved in the parent item's history.
        </p>
      </Section>

      <Section title="Full walkthrough">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li><Link href={pathFor("forms/sub-task")} className="text-cyan-300 hover:underline" data-testid="link-form-subtask">Create / complete a sub-task</Link> — the complete step-by-step.</li>
        </ul>
      </Section>

      <Callout variant="info" title="Who can do what">
        Creating sub-tasks needs an admin (General Admin or higher). Completing a sub-task is allowed for the assignee, the parent item's owner, or any admin. Deleting a sub-task is admin-only, and only if no work has been logged against it.
      </Callout>
    </div>
  );
}
